import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Modal from 'components/atoms/Modal'
import { useDispatch } from 'react-redux'

import {
  unitAccessApproveRequestRoutine,
  unitAccessDenyRoutine
} from 'ducks/units/actions'

import Select from 'components/atoms/Select'
import Input from 'components/atoms/Input'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import { hideEmail, hidePhoneNumber } from 'utils/user'
import { formatDate } from 'utils/date'
import { unitRoleLabel } from 'utils/roles'

const getAvatarColor = (name) => {
  const colors = ['#EF4444', '#F59E0B', '#10B981', '#33becc', '#6366F1', '#8B5CF6', '#EC4899']
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

const getInitials = (firstName, lastName, email) => {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()
  if (firstName) return firstName.substring(0, 2).toUpperCase()
  if (email) return email.substring(0, 2).toUpperCase()
  return 'U'
}

const RequestUnitBox = ({ request, bpId, unit }) => {
  const dispatch = useDispatch()
  const user = request?.user || {}

  const name = useMemo(() => {
    const n = [user.firstName, user.lastName].filter(Boolean).join(' ')
    return n || '—'
  }, [user.firstName, user.lastName])

  const email = user.email ? hideEmail(user.email) : '—'
  const phone = user.phoneNumber ? hidePhoneNumber(user.phoneNumber) : '—'
  const requestedAt = request?.createdAt ? formatDate(request.createdAt) : '—'

  const roleLabel = request?.relationType ? unitRoleLabel(request.relationType) : '-'

  const [openAccept, setOpenAccept] = useState(false)
  const [openReject, setOpenReject] = useState(false)
  const [reason, setReason] = useState('no_reason')
  const [other, setOther] = useState('')

  const doAccept = () => {
    dispatch(
      unitAccessApproveRequestRoutine.trigger({
        bpId,
        unitId: unit.id,
        type: 'request',
        requestId: request.id
      })
    )
    setOpenAccept(false)
  }

  const doReject = () => {
    const finalReason = reason === 'other' ? other : reason

    dispatch(
      unitAccessDenyRoutine.trigger({
        bpId,
        unitId: unit.id,
        type: 'request',
        requestId: request.id,
        reason: finalReason
      })
    )

    setOpenReject(false)
  }

  const initials = getInitials(user.firstName, user.lastName, user.email)
  const bgColor = getAvatarColor(user.email || name || initials)

  return (
    <>
      <TableRow>
        <TableCell data-label="Name">
          <NameCellContent>
            <TableAvatar style={{ background: bgColor }}>
              {initials}
            </TableAvatar>
            <NameText>{name}</NameText>
          </NameCellContent>
        </TableCell>
        <TableCell data-label="Email">{email}</TableCell>
        <TableCell data-label="Phone">{phone}</TableCell>
        <TableCell data-label="Role">{roleLabel}</TableCell>
        <TableCell data-label="Requested">{requestedAt}</TableCell>
        <TableCell>
          <Actions>
            <AcceptBtn onClick={() => setOpenAccept(true)}>Accept</AcceptBtn>
            <DenyBtn onClick={() => setOpenReject(true)}>Deny</DenyBtn>
          </Actions>
        </TableCell>
      </TableRow>

      <Modal
        title='Accept request'
        open={openAccept}
        onClose={() => setOpenAccept(false)}
        onCancel={() => setOpenAccept(false)}
        onSubmit={doAccept}
        withCancel
        withSubmit
      >
        Do you really want to accept this request?
      </Modal>

      <Modal
        title='Deny request'
        open={openReject}
        onClose={() => setOpenReject(false)}
        onCancel={() => setOpenReject(false)}
        onSubmit={doReject}
        withCancel
        withSubmit
        disabled={reason === 'other' && !other.trim()}
      >
        <InputDescription margin='0 0 10px 0'>
          Select a reason to send to the requester:
        </InputDescription>

        <Select
          name='reason'
          label='Reason'
          value={reason}
          options={[
            { label: 'Do not give reason', value: 'no_reason' },
            { label: 'I do not recognise this person', value: 'not_recognised' },
            { label: 'I do not wish to share building information with this person', value: 'dont_want_to_share' },
            { label: 'The information is sensitive and not able to be shared at present', value: 'information_is_sensitive' },
            { label: 'The person has applied under the wrong role', value: 'wrong_role' },
            { label: 'Other (enter the wording you wish the user to be sent)', value: 'other' }
          ]}
          onChange={(_, v) => setReason(v)}
        />

        {reason === 'other' && (
          <Input
            name='other'
            label='Your reason'
            multiline
            rows={3}
            value={other}
            onChange={(_, v) => setOther(v)}
          />
        )}
      </Modal>
    </>
  )
}

export default RequestUnitBox

const TableRow = styled.tr`
  display: table-row;

  &:hover {
    background-color: #f8fafc;
  }

  @media (max-width: 768px) {
    display: block;
    margin-bottom: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    background: white;
  }
`

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #525f7f;
  display: table-cell;
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;

  &:first-child {
    font-weight: 500;
  }

  &:last-child {
    text-align: right;
    padding-right: 12px;
  }

  @media (max-width: 1024px) {
    padding: 12px 16px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    display: block;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
    position: relative;
    padding-left: 120px;

    &:last-child {
      text-align: left;
      padding-left: 16px;
      width: auto;
      border-bottom: none;
    }

    &:first-child {
      font-weight: 600;
      border-bottom: 2px solid #e0e0e0;
      padding-top: 16px;
      padding-left: 16px;
    }

    &::before {
      content: attr(data-label);
      position: absolute;
      left: 16px;
      top: 12px;
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
      color: #666;
      letter-spacing: 0.5px;
    }

    &:first-child::before {
      display: none;
    }
  }

  @media (max-width: 640px) {
    padding: 10px 14px;
    padding-left: 100px;
    font-size: 13px;

    &::before {
      left: 14px;
      font-size: 11px;
    }

    &:last-child {
      padding-left: 14px;
    }

    &:first-child {
      padding-left: 14px;
    }
  }
`

const NameCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const TableAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const NameText = styled.span`
  font-weight: 500;
  color: #1e293b;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`

const AcceptBtn = styled.button`
  padding: 8px 16px;
  border: none;
  background: #10b981;
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #059669;
  }

  @media (max-width: 640px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`

const DenyBtn = styled.button`
  padding: 8px 16px;
  border: none;
  background: #d14724;
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b93c1d;
  }

  @media (max-width: 640px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`
